// src/oura/ask/actions.ts
//
// Ask Oura, chat actions (behind VITE_ENABLE_OURA_ASK_ACTIONS): "tick the meta
// description step on /botox", "mark missing image text fixed", "mark G2 done,
// we fixed the links", "draft the client note". The chat finds the thing on the
// client's Delivery records, asks the server what the action would change
// (preview), and shows that as a card with Do it. Nothing runs until a person
// presses Do it (AskOura.tsx). The server's list of actions decides what can run
// (ourteam-backend #6801); every action on it is reversible, and send, publish,
// delete and anything that spends money are not on it.

import type { DeliveryItem, Deliverable } from "@service/aeo/deliveryTypes";
import { listOuraActions, previewOuraAction, type OuraAction } from "@service/aeo/ask";
import { hrefs, p, s, src } from "./text";
import type { AskAnswer, AskBlock } from "./types";
import type { ClientData } from "./useAskData";

export interface ActionAsk {
    name: string;
    /** "Tick: Pages, then Menu, then Meta description, on /botox". */
    label: string;
    params: Record<string, unknown>;
}

/** What the words asked for: an action to preview, an answer instead (which one? none found), or nothing. */
export type ActionMatch = { kind: "action"; ask: ActionAsk } | { kind: "answer"; answer: AskAnswer } | null;

const TICK = /^\s*(?:please |pls )?(tick|untick)\b/;
const MARK_FIXED = /^\s*(?:please |pls )?mark\b.*\bfixed\b/;
const DONE = /^\s*(?:please |pls )?(?:(?:mark|close|log)\b.*\b(?:done|closed?|complete|proof)\b|add proof\b)/;
const NOTE = /^\s*(?:please |pls )?(?:draft|write|prepare)\b.*\b(?:client note|week'?s note|friday note|the note|a note|note to (?:the )?client)\b/;

/** True when the words ask for one of the chat's actions (an imperative, never a how-do-I question). */
export const looksLikeAction = (t: string): boolean => TICK.test(t) || MARK_FIXED.test(t) || DONE.test(t) || NOTE.test(t);

const STOP = new Set("a an the is are to of in on for at by and or me my i we us our it its this that please pls step the then".split(" "));
const words = (text: string): string[] =>
    text
        .toLowerCase()
        .replace(/[’']/g, "")
        .split(/[^a-z0-9/._-]+/)
        .filter((w) => w.length > 1 && !STOP.has(w))
        .map((w) => (w.startsWith("/") ? w : w.slice(0, 5)));

/** How many of the asked words the candidate's own words share. */
function overlap(asked: Set<string>, candidate: string): number {
    return new Set(words(candidate)).size === 0 ? 0 : Array.from(new Set(words(candidate))).filter((w) => asked.has(w)).length;
}

/** The one best candidate, or the ties for first when the words fit several equally (or none at all). */
function pick<T>(t: string, candidates: { text: string; value: T }[]): { best: T | null; ties: T[] } {
    const asked = new Set(words(t));
    const scored = candidates.map((c, i) => ({ ...c, i, score: overlap(asked, c.text) }));
    scored.sort((a, b) => b.score - a.score || a.i - b.i);
    const top = scored[0];
    if (!top || top.score === 0) return { best: null, ties: scored.slice(0, 4).map((x) => x.value) };
    const ties = scored.filter((x) => x.score === top.score);
    return ties.length === 1 ? { best: top.value, ties: [] } : { best: null, ties: ties.slice(0, 4).map((x) => x.value) };
}

const choose = (lead: string, items: string[]): AskAnswer => ({
    blocks: [p(s(lead)), { kind: "choices", items } as AskBlock],
    followUps: [],
});

const nothing = (d: ClientData, what: string): AskAnswer => ({
    blocks: [p(s(`${d.name} has no ${what} on Delivery right now.`, src("Delivery", hrefs.delivery(d.businessId))))],
    followUps: ["What should I do next?"],
});

/* ------------------------------------------------------------------------- *
 * The three Delivery actions
 * ------------------------------------------------------------------------- */

interface StepRow {
    item: DeliveryItem;
    deliverable: Deliverable;
    stepId: string;
    where: string;
    page: string | null;
    ticked: boolean;
}

function stepsOf(d: ClientData): StepRow[] {
    const out: StepRow[] = [];
    for (const deliverable of d.delivery?.deliverables ?? []) {
        for (const item of deliverable.items) {
            for (const st of item.pasteList?.steps ?? []) out.push({ item, deliverable, stepId: st.id, where: st.where, page: st.page ?? null, ticked: st.ticked });
        }
    }
    return out;
}

const stepText = (r: StepRow) => `${r.where}${r.page ? ` on ${r.page}` : ""} (${r.item.label})`;
const tickChip = (r: StepRow, untick: boolean) => `${untick ? "Untick" : "Tick"} "${r.where}"${r.page ? ` on ${r.page}` : ""} for ${r.item.label}`;

function tick(t: string, d: ClientData): ActionMatch {
    const untick = /^\s*(?:please |pls )?untick\b/.test(t);
    const rows = stepsOf(d).filter((r) => r.ticked === untick);
    if (rows.length === 0) return { kind: "answer", answer: nothing(d, untick ? "ticked paste step to untick" : "paste step left to tick") };
    const { best, ties } = pick(
        t,
        rows.map((r) => ({ text: `${r.where} ${r.page ?? ""} ${r.item.label} ${r.deliverable.code}`, value: r })),
    );
    if (!best) return { kind: "answer", answer: choose(`Which step? ${d.name} has ${rows.length} ${untick ? "ticked" : "to tick"}.`, ties.map((r) => tickChip(r, untick))) };
    return {
        kind: "action",
        ask: { name: "tick_step", label: `${untick ? "Untick" : "Tick"}: ${stepText(best)}`, params: { item_id: best.item.id, step_id: best.stepId, ticked: !untick } },
    };
}

function markFixed(t: string, d: ClientData): ActionMatch {
    const items = (d.delivery?.deliverables ?? []).flatMap((dl) => dl.items.filter((i) => i.kind === "check" && i.state !== "done").map((item) => ({ item, dl })));
    if (items.length === 0) return { kind: "answer", answer: nothing(d, "open check to mark fixed") };
    const { best, ties } = pick(
        t,
        items.map((x) => ({ text: `${x.item.label} ${x.item.technical ?? ""} ${(x.item.checkKey ?? "").replace(/_/g, " ")} ${x.item.pages.map((pg) => pg.url).join(" ")} ${x.dl.code}`, value: x })),
    );
    if (!best) return { kind: "answer", answer: choose(`Which check? ${d.name} has ${items.length} open.`, ties.map((x) => `Mark ${x.item.label} fixed`)) };
    const asked = new Set(words(t));
    const pages = best.item.pages.map((pg) => pg.url).filter((u) => asked.has(u.toLowerCase()) || asked.has(pathOf(u)));
    const params: Record<string, unknown> = { item_id: best.item.id };
    if (pages.length) params.pages = pages;
    return { kind: "action", ask: { name: "mark_fixed", label: `Mark fixed: ${best.item.label}${pages.length ? ` on ${pages.length === 1 ? pages[0] : `${pages.length} pages`}` : ""}`, params } };
}

const pathOf = (url: string): string => {
    try {
        return new URL(url).pathname.toLowerCase();
    } catch {
        return url.toLowerCase();
    }
};

function addProof(t: string, d: ClientData): ActionMatch {
    const open = (d.delivery?.deliverables ?? []).filter((dl) => dl.group !== "done");
    if (open.length === 0) return { kind: "answer", answer: nothing(d, "open line to close") };
    const code = /\b([a-z]\d{1,2})\b/i.exec(t)?.[1]?.toUpperCase() ?? null;
    const byCode = code ? open.filter((dl) => dl.code.toUpperCase() === code) : [];
    // The current month's line for a code first; otherwise the words decide.
    const month = d.delivery?.month ?? 0;
    const target = byCode.length ? (byCode.find((dl) => dl.month === month) ?? byCode[0]) : null;
    const { best, ties } = target ? { best: target, ties: [] as Deliverable[] } : pick(t, open.map((dl) => ({ text: `${dl.code} ${dl.label} ${dl.outlet ?? ""}`, value: dl })));
    if (!best) return { kind: "answer", answer: choose(`Which line? Say its code, like G2.`, ties.map((dl) => `Mark ${dl.code} ${dl.label} done`)) };
    const alreadyFine = /\balready (fine|ok|good|done|in place)\b|\bnothing to (do|fix)\b/.test(t);
    // "Mark S1 done, we fixed the headings": the clause after the comma (or "because", "note:") is the work logged.
    const note = /(?:,|:|;|\bbecause\b|\bnote\b|\bproof\b|\bwork logged\b)\s*(.{3,160}?)\s*[.!]?\s*$/i.exec(t)?.[1] ?? null;
    const params: Record<string, unknown> = { deliverable_key: best.key, kind: alreadyFine ? "already_fine" : "work" };
    if (note && !alreadyFine) params.note = note.replace(/^(that |the )/, "");
    const link = /https?:\/\/\S+/.exec(t)?.[0];
    if (link) params.link = link;
    return { kind: "action", ask: { name: "add_proof", label: `Close: ${best.code} ${best.label}${best.outlet ? ` (${best.outlet})` : ""}`, params } };
}

/* ------------------------------------------------------------------------- *
 * The match, and the preview
 * ------------------------------------------------------------------------- */

/** Which action the words ask for, found on the client's records. Null when they ask for none. */
export function actionIntent(t: string, d: ClientData): ActionMatch {
    if (NOTE.test(t)) return { kind: "action", ask: { name: "draft_client_note", label: "Draft this week's client note", params: {} } };
    if (TICK.test(t)) return tick(t, d);
    // "Mark G2 done, we fixed the links" closes a line; "mark missing image text fixed" marks a check.
    if (DONE.test(t)) return addProof(t, d);
    if (MARK_FIXED.test(t)) return markFixed(t, d);
    return null;
}

const cannot = (d: ClientData, why: string): AskAnswer => ({
    blocks: [p(s(`${why} Nothing was changed.`, src("Delivery", hrefs.delivery(d.businessId))))],
    followUps: ["What should I do next?"],
});

/**
 * The action as a card to say yes to: the server's own words on what it would
 * change. The server's list decides whether the action exists; a preview that
 * is refused comes back as one plain sentence.
 */
export async function actionPreviewAnswer(ask: ActionAsk, d: ClientData): Promise<AskAnswer> {
    const actions = await listOuraActions();
    if (!actions) return cannot(d, "Oura could not read which actions it may run just now.");
    const def: OuraAction | undefined = actions.find((a) => a.name === ask.name);
    if (!def) return cannot(d, `Oura cannot run "${ask.label}" from the chat on this server.`);
    const preview = await previewOuraAction(d.businessId, ask.name, ask.params);
    if (!preview.ok) return cannot(d, preview.message);
    return {
        blocks: [
            p(s("Here is what Oura would do. Nothing changes until you press Do it.", src("Delivery", hrefs.delivery(d.businessId)))),
            { kind: "action", businessId: d.businessId, name: ask.name, label: def.label, says: preview.says, params: ask.params, undo: def.undo ?? null },
        ],
        followUps: [],
    };
}
